import BigNumber from 'bignumber.js'
import { DeserializedPool, SerializedFarm, SerializedPool, SerializedUserBoostingInfo } from 'state/types'
import { deserializeToken } from 'state/user/hooks/helpers'

type UserData =
  | DeserializedPool['userData']
  | {
      boostingInfo?: SerializedUserBoostingInfo
      allowance: number | string
      stakingTokenBalance: number | string
      stakedBalance: number | string
      pendingReward: number | string
    }

export const transformUserData = (userData: UserData) => ({
  allowance: new BigNumber(userData?.allowance || 0),
  stakingTokenBalance: new BigNumber(userData?.stakingTokenBalance || 0),
  stakedBalance: new BigNumber(userData?.stakedBalance || 0),
  pendingReward: new BigNumber(userData?.pendingReward || 0),
  ...(!!userData?.boostingInfo && {
    boostingInfo: {
      amount: new BigNumber(userData.boostingInfo.amount || 0),
      boostingAmount: new BigNumber(userData.boostingInfo.boostingAmount || 0),
      initialBoostingRate: new BigNumber(userData.boostingInfo.initialBoostingRate || 0),
      initialCompoundRate: new BigNumber(userData.boostingInfo.initialCompoundRate || 0),
      principalAmount: new BigNumber(userData.boostingInfo.principalAmount || 0),
      shares: new BigNumber(userData.boostingInfo.shares || 0),
      stakedAt: new BigNumber(userData.boostingInfo.stakedAt || 0),
      unlockedBoostingAmount: new BigNumber(userData.boostingInfo.unlockedBoostingAmount || 0),
    },
  }),
})

export const transformPool = (pool: SerializedPool): DeserializedPool => {
  const { boostingInfo, totalStaked, userData, stakingToken, earningToken, ...rest } = pool

  return {
    ...rest,
    ...(!!boostingInfo && {
      boostingInfo: {
        ...boostingInfo,
        historicalBoostingRate: new BigNumber(boostingInfo.historicalBoostingRate),
        historicalCompoundRate: new BigNumber(boostingInfo.historicalCompoundRate),
        totalShares: new BigNumber(boostingInfo.totalShares),
        totalStakedCompounded: new BigNumber(boostingInfo.totalStakedCompounded),
        totalBoostings: new BigNumber(boostingInfo.totalBoostings),
        ...(!!boostingInfo.unstakedShares && {
          unstakedShares: new BigNumber(boostingInfo.unstakedShares),
        }),
      },
    }),
    stakingToken: deserializeToken(stakingToken),
    earningToken: deserializeToken(earningToken),
    userData: transformUserData(userData),
    totalStaked: new BigNumber(totalStaked),
  }
}

export const getTokenPricesFromFarm = (farms: SerializedFarm[]) =>
  farms.reduce((prices, farm) => {
    const quoteTokenAddress = farm.quoteToken.address.toLocaleLowerCase()
    const tokenAddress = farm.token.address.toLocaleLowerCase()
    /* eslint-disable no-param-reassign */
    prices[quoteTokenAddress] = prices[quoteTokenAddress] || new BigNumber(farm.quoteTokenPriceBusd || 0).toNumber()
    prices[tokenAddress] = prices[tokenAddress] || new BigNumber(farm.tokenPriceBusd || 0).toNumber()
    /* eslint-enable no-param-reassign */
    return prices
  }, {})
